import { useState, useEffect, useRef, useCallback } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { useSharedGroupStore } from '@/stores/sharedGroupStore'

/**
 * 각 공유 그룹의 안읽은 채팅 여부를 실시간 구독.
 * RTDB readPositions/{uid}/lastReadAt vs Firestore 그룹 문서의 lastChatAt 비교.
 */
export function useUnreadCounts(): Record<string, number> {
  const uid = useAuthStore((s) => s.user?.uid)
  const sharedGroups = useSharedGroupStore((s) => s.sharedGroups)
  const activeGroupId = useSharedGroupStore((s) => s.activeGroupId)
  const [counts, setCounts] = useState<Record<string, number>>({})
  const groupIds = sharedGroups.map((g) => g.id).sort().join(',')
  const sharedGroupsRef = useRef(sharedGroups)
  sharedGroupsRef.current = sharedGroups

  const lastReadMap = useRef<Record<string, string>>({})

  const recalcCount = useCallback((groupId: string) => {
    if (!uid) return
    const group = sharedGroupsRef.current.find((g) => g.id === groupId)
    if (!group) return

    const lastRead = lastReadMap.current[groupId] || ''
    const lastChatAt = (group as unknown as { lastChatAt?: string }).lastChatAt || ''

    const hasUnread = lastChatAt > lastRead
    const count = hasUnread ? 1 : 0

    setCounts((prev) => {
      if (prev[groupId] === count) return prev
      return { ...prev, [groupId]: count }
    })
  }, [uid])

  // RTDB readPositions 구독
  useEffect(() => {
    if (!uid || !groupIds) {
      setCounts({})
      return
    }

    let cancelled = false
    const unsubs: (() => void)[] = []

    async function subscribe() {
      const { getRtdbInstance } = await import('@/lib/firebase')
      const rtdb = await getRtdbInstance()
      const { ref, onValue } = await import('firebase/database')
      if (cancelled) return

      for (const group of sharedGroupsRef.current) {
        if (cancelled) break
        const gid = group.id

        // onValue가 반환하는 구독 해제 함수만 호출 — off(readRef)는 같은 경로의
        // 다른 컴포넌트 리스너까지 모두 제거하므로 사용하지 않는다
        const readRef = ref(rtdb, `chatGroups/${gid}/readPositions/${uid}`)
        const unsubRead = onValue(readRef, (snap) => {
          if (cancelled) return
          const data = snap.val()
          lastReadMap.current[gid] = data?.lastReadAt || ''
          recalcCount(gid)
        })
        unsubs.push(unsubRead)
      }
    }

    subscribe()
    return () => {
      cancelled = true
      unsubs.forEach((u) => u())
      lastReadMap.current = {}
    }
  }, [uid, groupIds, recalcCount])

  // sharedGroups 변경 시 (lastChatAt 업데이트) 카운트 재계산
  useEffect(() => {
    for (const group of sharedGroups) {
      recalcCount(group.id)
    }
  }, [sharedGroups, recalcCount])

  // 활성 그룹은 0으로 표시
  useEffect(() => {
    if (activeGroupId) {
      setCounts((prev) => {
        if (prev[activeGroupId] === 0) return prev
        return { ...prev, [activeGroupId]: 0 }
      })
    }
  }, [activeGroupId])

  return counts
}
